import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface DocViewerDrawerProps {
  open: boolean;
  onClose: () => void;
  docUrl: string;
  fileName?: string;
}

type FileCategory = 'office' | 'pdf' | 'image' | 'other';

/**
 * Determine how to render a file based on its extension.
 *  - office: .docx, .doc, .xlsx, .xls, .pptx, .ppt  -> Office Online iframe
 *  - pdf: .pdf                                         -> native browser iframe
 *  - image: .png, .jpg, .jpeg, .gif, .webp, .bmp, .svg -> <img> tag
 *  - other: anything else                               -> download/open fallback
 */
function categoriseFile(fileName: string): FileCategory {
  const ext = fileName.includes('.')
    ? fileName.split('.').pop()?.toLowerCase() ?? ''
    : '';

  const officeExts = ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'];
  if (officeExts.includes(ext)) return 'office';

  if (ext === 'pdf') return 'pdf';

  const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
  if (imageExts.includes(ext)) return 'image';

  return 'other';
}

const DocViewerDrawer: React.FC<DocViewerDrawerProps> = ({
  open,
  onClose,
  docUrl,
  fileName = 'Document'
}) => {
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [localBlobUrl, setLocalBlobUrl] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState<boolean>(false);
  const blobUrlRef = useRef<string | null>(null);

  const category = categoriseFile(fileName);

  // For non-office file types we still pre-fetch the document and create a
  // local blob URL so the browser can render it without CORS issues.
  // Office documents skip this entirely -- Office Online fetches them directly.
  useEffect(() => {
    if (!open || !docUrl) return;

    // Office Online handles its own fetching; no pre-fetch needed.
    if (category === 'office') {
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    setLocalBlobUrl(null);

    fetch(docUrl)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        if (cancelled) return;
        // Revoke previous blob URL if any
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setLocalBlobUrl(url);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('Failed to pre-fetch document for preview:', err);
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, docUrl, category]);

  // Reset iframe loaded state when drawer opens with a new doc
  useEffect(() => {
    if (open) {
      setIframeLoaded(false);
    }
  }, [open, docUrl]);

  // Clean up blob URL when drawer closes
  useEffect(() => {
    if (!open && blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
      setLocalBlobUrl(null);
    }
  }, [open]);

  const handleClose = () => {
    setError(false);
    setIframeLoaded(false);
    onClose();
  };

  const handleDownload = () => {
    window.open(docUrl, '_blank');
  };

  const handleOpenInNewTab = () => {
    window.open(docUrl, '_blank');
  };

  // Build the Office Online viewer URL using the ORIGINAL SAS URL
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docUrl)}`;

  // Render the document content area based on file category
  const renderContent = () => {
    // Show loading spinner while pre-fetching (non-office files)
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      );
    }

    // Show error fallback UI
    if (error) {
      return (
        <Box sx={{ maxWidth: '600px', margin: '0 auto', p: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Unable to preview this document in the browser.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This may be due to document format or security restrictions. Please use one of the options below:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenInNewTab}
              >
                Open in New Tab
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
                Download
              </Button>
            </Box>
          </Alert>
        </Box>
      );
    }

    switch (category) {
      // Office documents: render via Microsoft Office Online viewer iframe
      case 'office':
        return (
          <Box sx={{ position: 'relative', height: '100%' }}>
            {!iframeLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f5f5f5',
                  zIndex: 1
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <iframe
              src={officeViewerUrl}
              title={fileName}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              onLoad={() => setIframeLoaded(true)}
            />
          </Box>
        );

      // PDFs: render in a native browser iframe using the blob URL
      case 'pdf':
        if (!localBlobUrl) return null;
        return (
          <iframe
            src={localBlobUrl}
            title={fileName}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
          />
        );

      // Images: render with an <img> tag using the blob URL
      case 'image':
        if (!localBlobUrl) return null;
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              height: '100%',
              overflow: 'auto',
              p: 2
            }}
          >
            <img
              src={localBlobUrl}
              alt={fileName}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>
        );

      // Other files: show fallback download/open UI
      case 'other':
      default:
        return (
          <Box sx={{ maxWidth: '600px', margin: '0 auto', p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                This file type cannot be previewed in the browser.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please download the file or open it in a new tab.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpenInNewTab}
                >
                  Open in New Tab
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </Box>
            </Alert>
          </Box>
        );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: 11000 }} // above the claims drawer (9999), same as PDFViewerDrawer
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '80%', md: '60%', lg: '50%' },
          maxWidth: '800px'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, mr: 2 }}>
            {fileName}
          </Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: 1,
            borderColor: 'divider',
            gap: 1
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
          >
            Open in New Tab
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button>
        </Box>

        {/* Document Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
            p: (error || loading || category === 'other') ? 2 : 0
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Drawer>
  );
};

export default DocViewerDrawer;
